/*
  Adaptive (HLS) playback for Shopify-hosted videos.

  Shopify exposes a single MP4 per video (the 1080p one), but its HLS playlist (.m3u8)
  also carries 720p and 480p renditions. hls.js plays that playlist and is loaded on
  demand the first time a video is about to play.

  The rendition is capped to the smallest one that covers the element's rendered width
  AND height (x device pixel ratio, max 2), so portrait and landscape videos both get
  the right size, and playback always starts at the lightest rendition.

  Apple devices (Safari / iOS) play HLS natively and pick the rendition themselves.
  Browsers without HLS or Media Source support keep the MP4.

  Usage: window.VideoHLS.attach(videoElement, m3u8Url[, options]) -> Promise<boolean>
         (true when HLS is used, false when the caller should fall back to MP4)
  options.maxHeight (e.g. 720): never above that rendition ("720p"), also on Apple devices,
  where the matching rendition's own playlist is played instead of the full one. Without it,
  nothing changes.
*/
(function () {
  if (window.VideoHLS) return;

  // Exact version: the file is cached for a year ("@1" was only cached for a week)
  var HLS_SRC = 'https://cdn.jsdelivr.net/npm/hls.js@1.7.3/dist/hls.light.min.js';
  var MAX_PIXEL_RATIO = 2;
  var libraryPromise = null;

  function loadLibrary() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!libraryPromise) {
      libraryPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = HLS_SRC;
        script.async = true;
        script.onload = function () { window.Hls ? resolve(window.Hls) : reject(new Error('hls.js unavailable')); };
        script.onerror = function () { reject(new Error('hls.js failed to load')); };
        document.head.appendChild(script);
      });
    }
    return libraryPromise;
  }

  function isApple() {
    return /Apple/.test(navigator.vendor || '');
  }

  function supportsNativeHls(video) {
    return !!video.canPlayType('application/vnd.apple.mpegurl');
  }

  // Smallest rendition covering the element's rendered size (both dimensions)
  function capIndexFor(video, levels) {
    var ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    var rect = video.getBoundingClientRect();
    var width = (rect.width || video.clientWidth || 0) * ratio;
    var height = (rect.height || video.clientHeight || 0) * ratio;
    var index = levels.length - 1;
    for (var i = 0; i < levels.length; i++) {
      if (levels[i].width >= width && levels[i].height >= height) {
        index = i;
        break;
      }
    }
    return index;
  }

  // Index of the highest rendition at or below maxHeight (the lowest one if none is)
  function maxIndexFor(levels, maxHeight) {
    var index = 0;
    for (var i = 0; i < levels.length; i++) {
      if (levels[i].height && levels[i].height <= maxHeight && levels[i].height >= levels[index].height) index = i;
    }
    return index;
  }

  // Apple (native HLS): the playlist of the best rendition at or below maxHeight, from the main one
  function cappedPlaylist(url, maxHeight) {
    return fetch(url).then(function (response) {
      if (!response.ok) throw new Error('playlist');
      return response.text();
    }).then(function (text) {
      var lines = text.split(/\r?\n/);
      var best = null;
      for (var i = 0; i < lines.length; i++) {
        var match = /^#EXT-X-STREAM-INF:.*RESOLUTION=(\d+)x(\d+)/.exec(lines[i]);
        var bandwidth = /BANDWIDTH=(\d+)/.exec(lines[i]);
        var uri = lines[i + 1];
        if (!match || !uri || uri.charAt(0) === '#') continue;
        var height = parseInt(match[2], 10);
        var rate = bandwidth ? parseInt(bandwidth[1], 10) : 0;
        if (height <= maxHeight && (!best || rate > best.rate)) best = { uri: uri.trim(), rate: rate };
      }
      return best ? new URL(best.uri, new URL(url, window.location.href)).href : url;
    }).catch(function () {
      return url;
    });
  }

  function attachWithLibrary(video, url, options) {
    return loadLibrary().then(function (Hls) {
      if (!Hls.isSupported()) return false;

      var hls = new Hls({
        startLevel: 0,
        maxBufferLength: 8,
        maxMaxBufferLength: 16
      });
      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        var cap = capIndexFor(video, hls.levels);
        if (options && options.maxHeight) cap = Math.min(cap, maxIndexFor(hls.levels, options.maxHeight));
        hls.autoLevelCapping = cap;
      });
      hls.on(Hls.Events.ERROR, function (event, data) {
        if (data && data.fatal) {
          hls.destroy();
          video.dispatchEvent(new Event('error'));
        }
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      video._hls = hls;
      return true;
    });
  }

  function attach(video, url, options) {
    if (!video || !url) return Promise.resolve(false);

    if (isApple() && supportsNativeHls(video)) {
      if (options && options.maxHeight && window.fetch) {
        return cappedPlaylist(url, options.maxHeight).then(function (playlist) {
          video.src = playlist;
          return true;
        });
      }
      video.src = url;
      return Promise.resolve(true);
    }

    if (!window.MediaSource && !window.ManagedMediaSource) {
      if (supportsNativeHls(video)) {
        video.src = url;
        return Promise.resolve(true);
      }
      return Promise.resolve(false);
    }

    return attachWithLibrary(video, url, options).catch(function () {
      if (supportsNativeHls(video)) {
        video.src = url;
        return true;
      }
      return false;
    });
  }

  // Finds the HLS url in a <video> built with <source data-src> children
  function sourceFromChildren(video) {
    var sources = video.querySelectorAll('source');
    for (var i = 0; i < sources.length; i++) {
      var src = sources[i].dataset.src || sources[i].getAttribute('src') || '';
      var type = (sources[i].getAttribute('type') || '').toLowerCase();
      if (src.indexOf('.m3u8') !== -1 || type.indexOf('mpegurl') !== -1) return src;
    }
    return null;
  }

  // Loads hls.js ahead of time where it will be needed (not on Apple / native HLS)
  function warmup() {
    if (isApple()) return;
    if (!window.MediaSource && !window.ManagedMediaSource) return;
    loadLibrary().catch(function () {});
  }

  window.VideoHLS = {
    attach: attach,
    sourceFromChildren: sourceFromChildren,
    warmup: warmup
  };
})();
